import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { X, Factory, AlertCircle } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import apiClient from '@/api';
import { PopupField, PopupGridField, PopupCoordinates } from './PopupField';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { API_SOURCE_PATH } from './constants';
import ViewOnGoogleMaps from './ViewOnGoogleMaps';

export interface LoadPopupProps {
	longitude: number;
	latitude: number;
	properties: Record<string, string | number>;
	onClose: () => void;
}

interface LoadData {
	id: number;
	projectName: string;
	projectStatus: string;
	facilityCategory: string;
	facilityType: string;
	reportedInserviceYear: number;
	reportedInserviceMonth: number;
	powerCapacityMw: number;
	state: string;
	county: string;
	holdingCompanyOrParent: string;
	loadCenterOperator: string;
	lastYesEnergyUpdate: string;
	latitude: number;
	longitude: number;
}

const fetchLoadData = async (loadId: string | number): Promise<LoadData> => {
	const response = await apiClient.get(`${API_SOURCE_PATH}/loads/${loadId}`);
	return response.data;
};

export function LoadPopup({ longitude, latitude, properties, onClose }: LoadPopupProps) {
	const {
		data: fetchedData,
		isLoading,
		error
	} = useQuery({
		queryKey: ['geomap', 'load', properties.id],
		queryFn: () => fetchLoadData(properties.id),
		enabled: !!properties.id
	});

	const loadData: LoadData = {
		id: Number(properties.id) || 0,
		projectName: String(properties.projectName || ''),
		projectStatus: String(properties.projectStatus || ''),
		facilityCategory: String(properties.facilityCategory || ''),
		facilityType: String(properties.facilityType || ''),
		reportedInserviceYear: Number(properties.reportedInserviceYear) || 0,
		reportedInserviceMonth: Number(properties.reportedInserviceMonth) || 0,
		powerCapacityMw: Number(properties.powerCapacityMw) || 0,
		state: String(properties.state || ''),
		county: String(properties.county || ''),
		holdingCompanyOrParent: String(properties.holdingCompanyOrParent || ''),
		loadCenterOperator: String(properties.loadCenterOperator || ''),
		lastYesEnergyUpdate: String(properties.lastYesEnergyUpdate || ''),
		latitude: latitude,
		longitude: longitude,
		...(fetchedData || {})
	};

	const displayName = properties.name || `Plant ${properties.id}`;

	return (
		<Card className="bg-white dark:bg-slate-900 border shadow-lg min-w-[260px] max-w-[350px]">
			<CardHeader className="pb-2">
				<div className="flex items-center justify-between">
					<CardTitle className="flex items-center gap-2 text-sm">
						<Factory className="h-4 w-4" />
						Planned Plant Details
					</CardTitle>
					<Button aria-label="close" variant="ghost" size="icon" onClick={onClose} className="h-6 w-6">
						<X className="h-3 w-3" />
					</Button>
				</div>
			</CardHeader>
			<CardContent className="space-y-3">
				<PopupCoordinates longitude={longitude} latitude={latitude} />
				<div className="grid grid-cols-2 gap-2 text-xs">
					<PopupGridField label="ID" value={properties.id.toString()} />
					<PopupField label="Name" value={displayName} />
					<PopupField label="Facility Category" value={properties.facilityCategory} />
					<PopupField
						label="Capacity"
						value={properties.powerCapacityMw ? properties.powerCapacityMw.toString().concat(' MW') : 'N/A'}
					/>
				</div>
				<ViewOnGoogleMaps latitude={latitude} longitude={longitude} />
				{isLoading && (
					<div className="space-y-2">
						<Skeleton className="h-4 w-full" />
						<Skeleton className="h-4 w-3/4" />
						<Skeleton className="h-4 w-1/2" />
					</div>
				)}
				{error && (
					<div className="flex items-center gap-2 text-red-600 dark:text-red-400 text-xs">
						<AlertCircle className="h-3 w-3" />
						Failed to load additional data
					</div>
				)}
				{fetchedData && (
					<div className="space-y-2 pt-2 border-t border-slate-200 dark:border-slate-700">
						<PopupField label="Status" value={loadData.projectStatus} />
						<PopupField label="Facility Type" value={loadData.facilityType} />
						<PopupField
							label="Online Date"
							value={
								loadData.reportedInserviceYear && loadData.reportedInserviceMonth
									? `${loadData.reportedInserviceMonth.toString().padStart(2, '0')}/${loadData.reportedInserviceYear}`
									: 'N/A'
							}
						/>
						<PopupField label="County" value={loadData.county} />
						<PopupField label="State" value={loadData.state} />
						<PopupField label="Holding Company" value={loadData.holdingCompanyOrParent} />
						<PopupField label="Operator" value={loadData.loadCenterOperator} />
						<PopupField
							label="Last YES update"
							value={new Date(loadData.lastYesEnergyUpdate).toLocaleDateString('en-US')}
						/>
					</div>
				)}
			</CardContent>
		</Card>
	);
}
